module Meevo.Components.Common {

    export interface ICulture {
        name: string;
        currency: string;
        isoCurrencyCode: string;
        isoCountryCode: string;
        countryCode: string;
        locale: string;
        zipCodeRegExp: string;
        phoneNumberLength?: number;
        phoneNumberMask?: string;
    }

    export interface ICultureModel extends ICulture {
        testPhone(str: string): boolean;
        phoneWithCountryCode(str: string): boolean;
    }

    export class CultureModel implements ICultureModel {
        name: string;
        currency: string;
        isoCurrencyCode: string;
        isoCountryCode: string;
        countryCode: string;
        locale: string;
        zipCodeRegExp: string;
        phoneNumberLength?: number;
        phoneNumberMask?: string;

        constructor(culture: ICulture) {
            Object.assign(this, culture);
        }

        testPhone(str: string): boolean {
            return (str.match(/\d/g) || '').length === this.phoneNumberLength;
        }

        phoneWithCountryCode(str: string): boolean {
            const onlyNumbers = (str.match(/\d/g) || []).join('');

            return onlyNumbers.indexOf(this.countryCode.toString()) === 0 && onlyNumbers
                .replace(new RegExp(`^${this.countryCode}`), '') // remove country code
                .length === this.phoneNumberLength;
        }
    }

    export class CultureSettings {
        /**
         * @returns Meevo Available Countries Metadata
         */
        public static get Countries(): { [key: string]: ICulture } {
            return {
                US: {
                    name: 'United States',
                    currency: 'usdollar',
                    isoCurrencyCode: 'USD',
                    isoCountryCode: 'US',
                    countryCode: '1',
                    locale: 'en-US',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$',
                    phoneNumberLength: 10,
                    phoneNumberMask: '\d\d\d-\d\d\d-\d\d\d\d'
                },
                GB: {
                    name: 'United Kingdom',
                    currency: 'pound',
                    isoCurrencyCode: 'GBP',
                    isoCountryCode: 'GB',
                    countryCode: '44',
                    locale: 'en-GB',
                    zipCodeRegExp:
                        '^(([A-Z]{1,2}\\d[A-Z\\d]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?\\d[A-Z]{2}|BFPO ?\\d{1,4}|(KY\\d|MSR|VG|AI)[ -]?\\d{4}|[A-Z]{2} ?\\d{2}|GE ?CX|GIR ?0A{2}|SAN ?TA1)$',
                    phoneNumberLength: 11,
                    phoneNumberMask: '\d\d\d \d\d\d\d \d\d\d\d'
                },
                CA: {
                    name: 'Canada',
                    currency: 'cadollar',
                    isoCurrencyCode: 'CAD',
                    isoCountryCode: 'CA',
                    countryCode: '1',
                    locale: 'en-CA',
                    zipCodeRegExp: '^[ABCEGHJKLMNPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z][ ]?\\d[ABCEGHJ-NPRSTV-Z]\\d$$',
                    phoneNumberLength: 10,
                    phoneNumberMask: '\d\d\d-\d\d\d-\d\d\d\d'
                },
                AU: {
                    name: 'Australia',
                    currency: 'usdollar',
                    isoCurrencyCode: 'AUD',
                    isoCountryCode: 'AU',
                    countryCode: '61',
                    locale: 'en-AU',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                DE: {
                    name: 'Germany',
                    currency: 'euro',
                    isoCurrencyCode: 'EUR',
                    isoCountryCode: 'DE',
                    countryCode: '49',
                    locale: 'de-DE',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                JP: {
                    name: 'Japan',
                    currency: 'yen',
                    isoCurrencyCode: 'JPY',
                    isoCountryCode: 'JP',
                    countryCode: '81',
                    locale: 'ja',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                MX: {
                    name: 'Mexico',
                    currency: 'peso',
                    isoCurrencyCode: 'MXN',
                    isoCountryCode: 'ME',
                    countryCode: '52',
                    locale: 'es-MX',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                NZ: {
                    name: 'New Zealand',
                    currency: 'nzdollar',
                    isoCurrencyCode: 'NZD',
                    isoCountryCode: 'NZ',
                    countryCode: '64',
                    locale: 'en-NZ',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                NO: {
                    name: 'Norway',
                    currency: 'usdollar',
                    isoCurrencyCode: 'USD',
                    isoCountryCode: 'NO',
                    countryCode: '47',
                    locale: 'nb',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                ES: {
                    name: 'Spain',
                    currency: 'euro',
                    isoCurrencyCode: 'EUR',
                    isoCountryCode: 'ES',
                    countryCode: '34',
                    locale: 'es',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                CH: {
                    name: 'Switzerland',
                    currency: 'franc',
                    isoCurrencyCode: 'CHF',
                    isoCountryCode: 'CH',
                    countryCode: '41',
                    locale: 'fr-CH',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                TT: {
                    name: 'TrinidadAndTobago',
                    currency: 'ttdollar',
                    isoCurrencyCode: 'TTD',
                    isoCountryCode: 'TT',
                    countryCode: '1',
                    locale: 'en-TT',
                    zipCodeRegExp: '^\\d{5}([\\-]\\d{4})?$'
                },
                GI: {
                    name: 'Gibraltar',
                    currency: 'pound',
                    isoCurrencyCode: 'GIP',
                    isoCountryCode: 'GI',
                    countryCode: '350',
                    locale: 'en-GI',
                    zipCodeRegExp:
                        '^(([A-Z]{1,2}\\d[A-Z\\d]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) ?\\d[A-Z]{2}|BFPO ?\\d{1,4}|(KY\\d|MSR|VG|AI)[ -]?\\d{4}|[A-Z]{2} ?\\d{2}|GE ?CX|GIR ?0A{2}|SAN ?TA1)$',
                    phoneNumberLength: 11,
                    phoneNumberMask: '\d\d\d \d\d\d\d \d\d\d\d'
                },
            };
        }

        public static get CountriesISO() {
            return Object.keys(this.Countries);
        }

        /**
         * @returns Default Address Block Labels
         */
        public static get AddressLabels() {
            return {
                ADDRESS1: 'Address 1',
                ADDRESS2: 'Address 2',
                CITY: 'City',
                STATE: 'State',
                ZIPCODE: 'ZIP Code',
                COUNTRY: 'Country'
            };
        }

        public static cultureList(iso: string[] = []): ICulture[] {
            const countriesModelsList: ICulture[] = CultureSettings.CountriesISO.map((_iso) => CultureSettings.Countries[_iso]);

            return iso.length === 0 ? countriesModelsList : iso.map((_iso) => CultureSettings.Countries[_iso.toUpperCase()]);
        }

        public static cultureModelsList(iso: string[] = []): ICultureModel[] {
            return CultureSettings.cultureList(iso).map((culture) => new CultureModel(culture));
        }

        public static getByCountryCode(code: number | string): ICultureModel {
            return CultureSettings
                .cultureModelsList()
                .filter((culture) => culture.countryCode === code.toString())[0];
        }

        public static getByProperty(key: keyof ICulture, value: string): ICultureModel {
            return CultureSettings
                .cultureModelsList()
                .filter((culture) => (culture[key] as string).toLowerCase() === value.toLowerCase())[0];
        }
    }
}
